import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError, useAuth } from '@saas-kit/lib';
import { Button, ConfirmDialog, toast } from '@saas-kit/ui';
import { DataTable, type DataTableColumn } from '@saas-kit/ui/DataTable';

import {
  createCounter,
  deleteCounter,
  listAssignableStaff,
  listCounterGroups,
  listCounters,
  listServices,
  setCounterAssignments,
  setCounterServices,
  updateCounter,
} from './api';
import { CounterAssignmentsDrawer } from './CounterAssignmentsDrawer';
import { CounterCreateForm, type CounterCreateInput } from './CounterCreateForm';
import { CounterServicesDrawer } from './CounterServicesDrawer';
import type { QmsCounter } from './types';
import { useQmsCrud } from './useQmsCrud';

export function CountersTab({
  canManage,
  enabled,
}: {
  canManage: boolean;
  enabled: boolean;
}): JSX.Element {
  const { t } = useTranslation('qms');
  const tenantId = useAuth((s) => s.selectedTenant?.tenant_id);
  const queryClient = useQueryClient();
  const crud = useQmsCrud<QmsCounter, CounterCreateInput>({
    key: 'qms-counters',
    list: listCounters,
    create: createCounter,
    update: updateCounter,
    remove: deleteCounter,
    enabled,
  });
  const groupsQuery = useQuery({
    queryKey: ['qms-counter-groups', tenantId],
    queryFn: listCounterGroups,
    enabled,
  });
  const servicesQuery = useQuery({
    queryKey: ['qms-services', tenantId],
    queryFn: listServices,
    enabled,
  });
  const services = servicesQuery.data ?? [];
  const staffQuery = useQuery({
    queryKey: ['qms-assignable-staff', tenantId],
    queryFn: listAssignableStaff,
    enabled: enabled && canManage,
  });

  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [editing, setEditing] = useState<QmsCounter | null>(null);
  const [assigning, setAssigning] = useState<QmsCounter | null>(null);

  const mappingMutation = useMutation({
    mutationFn: ({ id, ids }: { id: string; ids: string[] }) =>
      setCounterServices(id, ids),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['qms-counters'] });
      toast.success(t('toasts.updated'));
      setEditing(null);
    },
    onError: (e) => toast.error(parseApiError(e).message),
  });
  const assignmentMutation = useMutation({
    mutationFn: ({ id, ids }: { id: string; ids: string[] }) =>
      setCounterAssignments(id, ids),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['qms-counters'] });
      toast.success(t('toasts.updated'));
      setAssigning(null);
    },
    onError: (e) => toast.error(parseApiError(e).message),
  });

  const columns: DataTableColumn<QmsCounter>[] = [
    { id: 'code', header: t('fields.code'), cell: (r) => r.code },
    { id: 'name', header: t('fields.name'), cell: (r) => r.name },
    {
      id: 'services',
      header: t('fields.services'),
      align: 'end',
      cell: (r) => r.service_ids.length,
    },
    {
      id: 'assigned',
      header: t('fields.assigned_staff'),
      align: 'end',
      cell: (r) => r.assigned_user_ids.length || t('assignments.none'),
    },
    {
      id: 'status',
      header: t('fields.status'),
      cell: (r) => (r.is_active ? t('status.active') : t('status.inactive')),
    },
    {
      id: 'actions',
      header: '',
      align: 'end',
      exportSkip: true,
      cell: (r) =>
        canManage ? (
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditing(r)}>
              {t('fields.services')}
            </Button>
            <Button variant="ghost" onClick={() => setAssigning(r)}>
              {t('fields.assigned_staff')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => crud.update(r.id, { is_active: !r.is_active })}
            >
              {r.is_active ? t('actions.deactivate') : t('actions.activate')}
            </Button>
            <Button variant="ghost" onClick={() => setPendingDelete(r.id)}>
              {t('actions.delete')}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      {canManage ? (
        <CounterCreateForm
          groups={groupsQuery.data ?? []}
          services={services}
          creating={crud.creating}
          onCreate={(input) => crud.create(input)}
        />
      ) : null}

      <DataTable
        data={crud.items}
        columns={columns}
        rowKey={(r) => r.id}
        selectable={canManage}
        isLoading={crud.isLoading}
        error={crud.isError ? t('errors.view_denied') : null}
        emptyText={t('empty')}
        bulkActions={
          canManage
            ? [
                {
                  id: 'delete',
                  label: t('actions.delete'),
                  variant: 'destructive',
                  onRun: (rows) => crud.removeMany(rows.map((r) => r.id)),
                },
              ]
            : []
        }
      />

      <CounterServicesDrawer
        counter={editing}
        services={services}
        saving={mappingMutation.isPending}
        onClose={() => setEditing(null)}
        onSave={(id, ids) => mappingMutation.mutate({ id, ids })}
      />

      <CounterAssignmentsDrawer
        counter={assigning}
        staff={staffQuery.data ?? []}
        saving={assignmentMutation.isPending}
        onClose={() => setAssigning(null)}
        onSave={(id, ids) => assignmentMutation.mutate({ id, ids })}
      />

      {pendingDelete ? (
        <ConfirmDialog
          title={t('confirm.delete_title')}
          body={t('confirm.delete_body')}
          confirmLabel={t('confirm.confirm')}
          cancelLabel={t('confirm.cancel')}
          variant="destructive"
          isBusy={crud.removing}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            crud.remove(pendingDelete);
            setPendingDelete(null);
          }}
        />
      ) : null}
    </div>
  );
}
